<script setup lang="ts">
import { ElCol, ElRow } from 'element-plus'

import { ImagePreview } from '@/shared/ui/ImagePreview'
import { Inline } from '@/shared/ui/Inline'
import { LoadErrorAlert } from '@/shared/ui/LoadErrorAlert'
import { Skeleton } from '@/shared/ui/Skeleton'
import { Stack } from '@/shared/ui/Stack'
import { Tag } from '@/shared/ui/Tag'
import { WidgetShell } from '@/shared/ui/WidgetShell'

import { TelegramAccountActions } from '@/apps/adok/features/telegram-account/manage'

import telegramAccountImages from '../assets'
import { useTelegramAccountPanel } from '../model/useTelegramAccountPanel'

const { connected, username, isLoading, loadError, isRetrying, retry } =
  useTelegramAccountPanel()
</script>

<template>
  <WidgetShell
    title="Telegram"
    hint="Only what needs an answer is sent — the bot does not post reports or news."
  >
    <LoadErrorAlert
      v-if="loadError"
      :message="loadError"
      :retrying="isRetrying"
      @retry="retry"
    />

    <Stack v-else-if="isLoading" :gap="12">
      <Skeleton width="180px" />
      <Skeleton variant="control" />
    </Stack>

    <ElRow v-else :gutter="20">
      <ElCol :span="10" :xs="24">
        <Stack :gap="12">
          <div>
            We write to Telegram when something waits for you: a domain that
            started being blocked, a balance that ran out, a check that came
            back.
          </div>

          <Inline block justify="center">
            <ImagePreview
              :src="telegramAccountImages.telegram"
              width="96"
              transparent
            />
          </Inline>
        </Stack>
      </ElCol>

      <ElCol :span="14" :xs="24">
        <Stack :gap="16">
          <div>
            Status:
            <Tag :type="connected ? 'success' : 'info'" size="small">
              {{ connected ? 'Connected' : 'Not connected' }}
            </Tag>
          </div>

          <div v-if="connected">
            Messages go to <b>@{{ username }}</b
            >. Disconnect and they stop — nothing else about the account
            changes.
          </div>
          <div v-else>
            The button opens our bot in Telegram. Press Start there and the
            account is linked; nothing is posted to your chats.
          </div>

          <Inline :gap="8">
            <TelegramAccountActions :connected="connected" />
          </Inline>
        </Stack>
      </ElCol>
    </ElRow>
  </WidgetShell>
</template>
